import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PasswordField } from "@/components/auth/password-field";
import { ForgotPasswordDialog } from "@/components/auth/forgot-password-dialog";
import { changePassword, logoutBoide, verifyCurrentPassword } from "@/lib/boide-api";
import { knownHasPassword, markHasPassword, validateNewPassword } from "@/lib/password";
import { clearBoideUser, useBoideUser } from "@/hooks/use-boide-user";
import { signOutFirebase } from "@/lib/firebase";
import { errorName, trackEvent } from "@/lib/analytics";

/**
 * Đổi mật khẩu (tài khoản đã có) hoặc Thêm mật khẩu (tài khoản Google/Apple).
 *
 * 🔴 ĐỔI XONG LÀ ĐĂNG XUẤT, KHÔNG PHẢI LỰA CHỌN CỦA MÀN NÀY: BE dọn cache đăng nhập
 * sau khi đặt mật khẩu (`repo_purge_user_cache`) nên token trên tay đã chết ngay tại
 * đó. Không đăng xuất thì người dùng ở lại một app trông như vẫn đăng nhập, rồi mọi
 * thao tác kế tiếp đổ 401 không rõ nguyên do.
 *
 * 🔴 BẮT NHẬP MẬT KHẨU HIỆN TẠI LÀ VIỆC CỦA FE — BE `/v1/users/reset-password` chỉ
 * nhận `new_password` và không kiểm gì. Ở đây kiểm bằng cách gọi thẳng endpoint đăng
 * nhập (`verifyCurrentPassword`). Hàng rào này chặn người mượn được máy/phiên đang
 * mở, KHÔNG chặn được ai gọi thẳng API — muốn chặn hẳn thì phải sửa BE.
 */
export function ChangePasswordDialog({
  open,
  onOpenChange,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Gọi sau khi đổi xong — để dialog/sheet cha đóng theo trước khi rời trang. */
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useBoideUser();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  /** Người dùng tự khai "tôi đã có mật khẩu" khi màn này đoán là chưa có. */
  const [forceCurrent, setForceCurrent] = useState(false);

  const username = user?.username ?? "";

  // `login_type` chỉ nói tài khoản được TẠO ra kiểu gì — xem ghi chú ở
  // `knownHasPassword`, nơi giải thích vì sao phải có thêm cờ cục bộ.
  const hasPassword = useMemo(
    () => forceCurrent || user?.login_type === "basic" || knownHasPassword(username),
    [forceCurrent, user?.login_type, username],
  );

  useEffect(() => {
    if (open) return;
    setCurrent("");
    setNext("");
    setConfirm("");
    setForceCurrent(false);
  }, [open]);

  const submit = async () => {
    const error = validateNewPassword(next, confirm);
    if (error) {
      toast.error(error);
      return;
    }
    if (hasPassword && !current) {
      toast.error("Vui lòng nhập mật khẩu hiện tại.");
      return;
    }
    if (hasPassword && current === next) {
      toast.error("Mật khẩu mới phải khác mật khẩu hiện tại.");
      return;
    }

    setSaving(true);
    try {
      if (hasPassword) {
        // Kiểm TRƯỚC khi đổi: sai mật khẩu cũ thì dừng ở đây, chưa đụng gì tới tài khoản.
        const ok = await verifyCurrentPassword(username, current);
        if (!ok) {
          toast.error("Mật khẩu hiện tại không đúng.");
          return;
        }
      }

      await changePassword(next);
      markHasPassword(username);
      trackEvent(hasPassword ? "auth_change_password" : "auth_add_password");

      // Token đã chết ở BE ngay khi đổi xong → dọn sạch phiên trước khi rời trang.
      logoutBoide();
      clearBoideUser();
      await signOutFirebase();

      onOpenChange(false);
      onChanged?.();
      toast.success(
        hasPassword
          ? "Đã đổi mật khẩu. Vui lòng đăng nhập lại bằng mật khẩu mới."
          : "Đã đặt mật khẩu. Từ giờ bạn có thể đăng nhập bằng email và mật khẩu.",
        { duration: 7000 },
      );
      void navigate({ to: "/login" });
    } catch (err) {
      trackEvent("auth_change_password_failed", { error_name: errorName(err) });
      toast.error(err instanceof Error ? err.message : "Không đổi được mật khẩu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => (saving ? null : onOpenChange(v))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-[0.12em]">
              <KeyRound className="size-5 text-primary" />
              {hasPassword ? "ĐỔI MẬT KHẨU" : "THÊM MẬT KHẨU"}
            </DialogTitle>
            <DialogDescription className="font-serif-soft italic">
              {hasPassword
                ? "Nhập mật khẩu hiện tại để xác nhận là bạn, rồi đặt mật khẩu mới."
                : "Tài khoản của bạn đang đăng nhập bằng Google/Apple. Đặt thêm mật khẩu để có thể đăng nhập bằng email."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {hasPassword ? (
              <div className="space-y-1.5">
                <PasswordField
                  label="Mật khẩu hiện tại"
                  value={current}
                  onChange={setCurrent}
                  autoComplete="current-password"
                  disabled={saving}
                  autoFocus
                />
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setForgotOpen(true)}
                  className="text-[11px] text-primary underline-offset-2 hover:underline"
                >
                  Không nhớ mật khẩu hiện tại?
                </button>
              </div>
            ) : null}

            <PasswordField
              label="Mật khẩu mới"
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              disabled={saving}
              showStrength
              autoFocus={!hasPassword}
              hint="Ít nhất 8 ký tự, có cả chữ và số, không dấu tiếng Việt."
            />
            <PasswordField
              label="Nhập lại mật khẩu mới"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={saving}
            />

            <p className="flex gap-2 rounded-lg border border-gold-soft/60 bg-background/40 p-3 text-[11px] text-muted-foreground">
              <ShieldCheck className="mt-[1px] size-4 shrink-0 text-primary" />
              <span>
                Sau khi đổi, mọi thiết bị đang đăng nhập sẽ bị đăng xuất — kể cả thiết bị này. Bạn
                sẽ được đưa về màn đăng nhập.
              </span>
            </p>

            {!hasPassword ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => setForceCurrent(true)}
                className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Tài khoản này đã có mật khẩu — tôi muốn nhập mật khẩu hiện tại.
              </button>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}>
              Huỷ
            </Button>
            <Button onClick={() => void submit()} disabled={saving}>
              {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              {saving ? "Đang lưu..." : hasPassword ? "Đổi mật khẩu" : "Đặt mật khẩu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultUsername={user?.email ?? username}
      />
    </>
  );
}
